import React from "react";
import { Link, useParams } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { base44 } from "@/api/base44Client";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Calendar, Clock } from "lucide-react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import "../styles/article.css";

export default function ArticleDetail() {
  const { slug } = useParams();

  const { data: articles = [], isLoading } = useQuery({
    queryKey: ['articles', slug],
    queryFn: () => base44.entities.Article.list()
  });

  const article = articles.find(a => a.slug === slug);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-600 text-lg">Chargement de l'article...</p>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Article introuvable</h1>
          <Link to={createPageUrl("Blog")}>
            <Button>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour au blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <div className="bg-gradient-to-br from-blue-900 to-purple-900 text-white py-12">
        <div className="max-w-4xl mx-auto px-4">
          <Link to={createPageUrl("Blog")}>
            <Button variant="ghost" className="text-white hover:bg-white/10 mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour au blog
            </Button>
          </Link>
          
          <Badge className="mb-4 bg-blue-500 text-white">
            {article.category}
          </Badge>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            {article.title}
          </h1>
          
          <div className="flex items-center gap-6 text-blue-100">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {new Date(article.created_date).toLocaleDateString('fr-FR', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {article.read_time} min de lecture
            </div>
          </div>
        </div>
      </div>

      {/* Image */}
      {article.image_url && (
        <div className="max-w-4xl mx-auto px-4 -mt-8">
          <img 
            src={article.image_url}
            alt={article.title}
            className="w-full h-96 object-cover rounded-2xl shadow-2xl"
          />
        </div>
      )}

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <style dangerouslySetInnerHTML={{__html: `
            .article-content h2 {
              margin-top: 5rem !important;
              margin-bottom: 2.5rem !important;
              font-size: 1.875rem !important;
              font-weight: 700 !important;
              line-height: 1.3 !important;
              border-bottom: 2px solid #e2e8f0 !important;
              padding-bottom: 1rem !important;
            }
            .article-content h3 {
              margin-top: 4rem !important;
              margin-bottom: 2rem !important;
              font-size: 1.5rem !important;
              font-weight: 700 !important;
              line-height: 1.4 !important;
            }
            .article-content p {
              margin-top: 2.5rem !important;
              margin-bottom: 2.5rem !important;
              font-size: 1.125rem !important;
              line-height: 2 !important;
              color: #334155 !important;
            }
            .article-content ul,
            .article-content ol {
              margin-top: 2.5rem !important;
              margin-bottom: 2.5rem !important;
              padding-left: 1.5rem !important;
            }
            .article-content li {
              margin-top: 1.5rem !important;
              margin-bottom: 1.5rem !important;
              font-size: 1.125rem !important;
              line-height: 1.8 !important;
              color: #334155 !important;
            }
            .article-content strong {
              font-weight: 600 !important;
              color: #0f172a !important;
            }
            .article-content a {
              color: #2563eb !important;
              font-weight: 500 !important;
              text-decoration: underline !important;
            }
            .article-content a:hover {
              color: #1d4ed8 !important;
            }
            .article-content img {
              margin-top: 3rem !important;
              margin-bottom: 3rem !important;
              border-radius: 0.5rem !important;
            }
            .article-content hr {
              margin-top: 4rem !important;
              margin-bottom: 4rem !important;
              border-color: #e2e8f0 !important;
            }
            .article-content table {
              margin-top: 3rem !important;
              margin-bottom: 3rem !important;
              width: 100% !important;
              border-collapse: collapse !important;
            }
            .article-content th,
            .article-content td {
              padding: 1rem 1.5rem !important;
              border: 1px solid #cbd5e1 !important;
            }
            .article-content th {
              background-color: #f1f5f9 !important;
              font-weight: 600 !important;
            }
            .article-content blockquote {
              margin-top: 2.5rem !important;
              margin-bottom: 2.5rem !important;
              padding: 1.5rem !important;
              background-color: #f8fafc !important;
              border-left: 4px solid #3b82f6 !important;
              border-radius: 0.5rem !important;
              font-style: italic !important;
            }
          `}} />
          <div className="article-content prose prose-lg max-w-none">
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
            >
              {article.content}
            </ReactMarkdown>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">
            Prêt à Démarrer Votre Parcours Crypto ?
          </h3>
          <p className="text-blue-100 mb-6">
            Bénéficiez d'un accompagnement personnalisé et de nos liens de parrainage exclusifs
          </p>
          <Link to={createPageUrl("Contact")}>
            <Button size="lg" className="bg-white text-blue-900 hover:bg-blue-50">
              Contactez-nous
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
